import { StorageManager } from '../storage/index.js';

/**
 * A forced update: when a server change would break older extensions, the owner raises the
 * minimum version (app_config `min_extension_version`) once the new version is live in the
 * Chrome Web Store. An older extension is then refused by the sync; it remembers that here,
 * asks Chrome for the update, and shows "LingoPing needs an update" until it has it.
 */

const REQUIRED_KEY = 'lingoping_update_required';
/** The minimum the server last named (the website warns about an older installed extension). */
const MIN_KEY = 'lingoping_min_extension_version';

export interface UpdateRequired {
  /** The oldest version the server works with. */
  minVersion: string | null;
  /** This extension's version. */
  version: string | null;
  at: string;
}

export async function getUpdateRequired(): Promise<UpdateRequired | null> {
  return StorageManager.getLocalValue<UpdateRequired>(REQUIRED_KEY);
}

export async function setUpdateRequired(minVersion: string | null): Promise<void> {
  const runtime = typeof chrome !== 'undefined' ? chrome.runtime : undefined;
  await StorageManager.setLocalValue<UpdateRequired>(REQUIRED_KEY, {
    minVersion,
    version: runtime?.getManifest?.().version ?? null,
    at: new Date().toISOString(),
  });
}

/** After a sync went through: this version is fine (again). */
export async function clearUpdateRequired(): Promise<void> {
  if (await getUpdateRequired()) await StorageManager.setLocalValue(REQUIRED_KEY, null);
}

export async function rememberMinExtensionVersion(min: string | null): Promise<void> {
  const known = await StorageManager.getLocalValue<string>(MIN_KEY);
  if ((known ?? null) !== min) await StorageManager.setLocalValue(MIN_KEY, min);
}

export async function getMinExtensionVersion(): Promise<string | null> {
  return StorageManager.getLocalValue<string>(MIN_KEY);
}

export type UpdateCheck = 'updating' | 'no_update' | 'throttled' | 'unavailable';

/**
 * Asks Chrome for a newer version now (it otherwise checks every few hours). When one is there,
 * the background worker applies it at once (`chrome.runtime.onUpdateAvailable`), which reloads
 * the extension.
 */
export async function checkForUpdate(): Promise<UpdateCheck> {
  const runtime = typeof chrome !== 'undefined' ? chrome.runtime : undefined;
  if (typeof runtime?.requestUpdateCheck !== 'function') return 'unavailable';
  try {
    const result = (await runtime.requestUpdateCheck()) as unknown as
      { status?: string } | string | undefined;
    const status = typeof result === 'string' ? result : result?.status;
    if (status === 'update_available') return 'updating';
    if (status === 'throttled') return 'throttled';
    return 'no_update';
  } catch {
    return 'unavailable';
  }
}
